import React, { useEffect, useMemo, useRef, useState } from 'react';
import { QRCodeCanvas } from 'qrcode.react';
import { api, useAuth, formatISTDate } from '../lib/store';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Plus, Pencil, Trash2, RefreshCw, Printer, QrCode, Copy, Search, ScanLine, Download, Layers, FileText } from 'lucide-react';
import { toast } from 'sonner';
import { logger } from '../lib/logger';

export default function QrCodesPage() {
  const { user } = useAuth();
  // Open to every authenticated role except dispatcher — staff & creators
  // must also view/upload/edit price-list QRs per owner's request.
  const canManage = !!user && user?.role !== 'dispatcher';

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  // Create dialog
  const [showCreate, setShowCreate] = useState(false);
  const [newLabel, setNewLabel] = useState('');
  const newFileRef = useRef(null);
  const [busy, setBusy] = useState(false);

  // Edit-label dialog
  const [editing, setEditing] = useState(null);
  const [editLabel, setEditLabel] = useState('');

  // Replace-image (uses hidden input, one per row)
  const replaceFileRef = useRef({});

  // Print dialog
  const [printing, setPrinting] = useState(null);
  const [printCfg, setPrintCfg] = useState({ sticker_w_mm: 15, sticker_h_mm: 20, count: 0, margin_mm: 5, gap_mm: 1, label_override: '' });

  // Search filter (by label)
  const [search, setSearch] = useState('');

  // QR-preview dialog (client-side rendered — instant, never fails)
  const [previewing, setPreviewing] = useState(null);
  const qrCanvasRef = useRef(null);

  // Bulk Print dialog
  const [showBulkPrint, setShowBulkPrint] = useState(false);
  const [bulkSize, setBulkSize] = useState({ sticker_w_mm: 15, sticker_h_mm: 20, margin_mm: 5, gap_mm: 1 });
  const [bulkRows, setBulkRows] = useState({}); // { qrId: { count, label_override } }

  // Multi-select on the dashboard — set of selected QR ids for Bulk Print
  const [selectedIds, setSelectedIds] = useState(() => new Set());

  // Pagination
  const PAGE_SIZE = 30;
  const [page, setPage] = useState(1);
  useEffect(() => { setPage(1); }, [search]); // reset to page 1 when search changes

  const toggleSelected = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const selectAllVisible = () => setSelectedIds(new Set(filteredItems.map(it => it.id)));
  const clearSelection = () => setSelectedIds(new Set());

  // Thumbnails — blob URLs keyed by id
  const [thumbs, setThumbs] = useState({});

  const filteredItems = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter(it => (it.label || '').toLowerCase().includes(q));
  }, [items, search]);

  // Derived pagination — must come after `filteredItems` is declared.
  const totalPages = Math.max(1, Math.ceil(filteredItems.length / PAGE_SIZE));
  const pageItems = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return filteredItems.slice(start, start + PAGE_SIZE);
  }, [filteredItems, page]);

  const fetchItems = async () => {
    setLoading(true);
    try {
      const r = await api.listQrCodes();
      setItems(r.data);
      // Load thumbnails in parallel: images get the original; PDFs get the
      // cached first-page JPEG thumbnail (when available).
      r.data.forEach(async (it) => {
        const isPdf = (it.content_type || '').toLowerCase() === 'application/pdf';
        try {
          const blobResp = isPdf
            ? await api.getQrThumbnailBlob(it.id)
            : await api.getQrImageBlob(it.id);
          const url = URL.createObjectURL(blobResp.data);
          setThumbs(t => ({ ...t, [it.id]: url }));
        } catch { /* PDF without thumbnail (no poppler) — placeholder icon shown */ }
      });
    } catch (e) {
      toast.error('Failed to load QR codes');
    } finally {
      setLoading(false);
    }
  };

  // Track latest thumbs in a ref so the unmount cleanup can revoke every
  // blob URL without re-running the mount effect on each thumbnail load.
  const thumbsRef = useRef({});
  useEffect(() => { thumbsRef.current = thumbs; }, [thumbs]);

  useEffect(() => {
    fetchItems();
    return () => {
      // Revoke blob URLs on unmount
      Object.values(thumbsRef.current).forEach(u => URL.revokeObjectURL(u));
    };
  }, []);

  const handleCreate = async () => {
    const file = newFileRef.current?.files?.[0];
    if (!file) { toast.error('Pick a price-list image first'); return; }
    const fd = new FormData();
    fd.append('image', file);
    fd.append('label', newLabel);
    setBusy(true);
    try {
      await api.createQrCode(fd);
      toast.success('QR code created');
      setShowCreate(false);
      setNewLabel('');
      if (newFileRef.current) newFileRef.current.value = '';
      fetchItems();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Create failed');
    } finally {
      setBusy(false);
    }
  };

  const handleReplaceImage = async (id, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append('image', file);
    setBusy(true);
    try {
      await api.replaceQrImage(id, fd);
      toast.success('Image replaced — same QR still works');
      // Revoke old thumb and reload
      if (thumbs[id]) URL.revokeObjectURL(thumbs[id]);
      setThumbs(t => { const n = { ...t }; delete n[id]; return n; });
      const img = await api.getQrImageBlob(id);
      setThumbs(t => ({ ...t, [id]: URL.createObjectURL(img.data) }));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Replace failed');
    } finally {
      setBusy(false);
    }
  };

  const handleSaveLabel = async () => {
    if (!editing) return;
    setBusy(true);
    try {
      await api.updateQrLabel(editing.id, editLabel);
      toast.success('Label saved');
      setEditing(null);
      fetchItems();
    } catch (e) {
      toast.error('Failed to save label');
    } finally { setBusy(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this QR code? Printed stickers for it will stop working.')) return;
    try {
      await api.deleteQrCode(id);
      toast.success('Deleted');
      fetchItems();
    } catch { toast.error('Delete failed'); }
  };

  const handlePrint = async () => {
    if (!printing) return;
    setBusy(true);
    try {
      const r = await api.generateQrPrintPdf(printing.id, printCfg);
      const blob = new Blob([r.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `QR_Stickers_${(printing.label || printing.id).replace(/[^\w-]+/g, '_')}.pdf`;
      // Firefox & some Chromium variants need the anchor in the DOM for .click()
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      // Revoke after the download has had a chance to start
      setTimeout(() => URL.revokeObjectURL(url), 4000);
      toast.success(`Sticker sheet generated (${r.headers['x-sticker-grid'] || ''})`);
      setPrinting(null);
    } catch (e) {
      const msg = e.response?.data?.detail || e.message || 'PDF generation failed';
      toast.error(typeof msg === 'string' ? msg : 'PDF generation failed');
      logger.error('Print PDF error:', e);
    } finally { setBusy(false); }
  };

  const copyUrl = async (url) => {
    try { await navigator.clipboard.writeText(url); toast.success('URL copied'); }
    catch { toast.error('Copy failed'); }
  };

  const openQrPreview = (it) => {
    // Client-side render — no server call, instant.
    setPreviewing(it);
  };

  const closeQrPreview = () => setPreviewing(null);

  const downloadQrPng = () => {
    if (!previewing) return;
    const canvas = qrCanvasRef.current?.querySelector('canvas');
    if (!canvas) return;
    const url = canvas.toDataURL('image/png');
    const a = document.createElement('a');
    a.href = url;
    a.download = `QR_${(previewing.label || previewing.id).replace(/[^\w-]+/g, '_')}.png`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const openBulkPrint = () => {
    if (selectedIds.size === 0) {
      toast.error('Select at least one image first (checkbox on each card)');
      return;
    }
    // Seed each SELECTED item with a default count of 10 and its saved label
    const seed = {};
    items.forEach(it => {
      if (selectedIds.has(it.id)) {
        seed[it.id] = { count: 10, label_override: it.label || '' };
      }
    });
    setBulkRows(seed);
    setShowBulkPrint(true);
  };

  const updateBulkRow = (id, patch) => {
    setBulkRows(prev => ({ ...prev, [id]: { ...(prev[id] || { count: 0, label_override: '' }), ...patch } }));
  };

  const handleBulkPrint = async () => {
    const payload = {
      ...bulkSize,
      items: Object.entries(bulkRows)
        .filter(([, v]) => (v.count || 0) > 0)
        .map(([id, v]) => ({ id, count: parseInt(v.count, 10), label_override: v.label_override || '' })),
    };
    if (!payload.items.length) { toast.error('Set count > 0 for at least one image'); return; }
    setBusy(true);
    try {
      const r = await api.generateBulkQrPrintPdf(payload);
      const blob = new Blob([r.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `QR_Stickers_Bulk_${payload.items.length}_items.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 4000);
      toast.success(`Generated ${r.headers['x-total-stickers'] || ''} stickers (${r.headers['x-sticker-grid'] || ''})`);
      setShowBulkPrint(false);
    } catch (e) {
      logger.error('Bulk print error:', e);
      toast.error('Bulk print failed');
    } finally { setBusy(false); }
  };

  const bulkTotal = Object.values(bulkRows).reduce((sum, v) => sum + (parseInt(v?.count, 10) || 0), 0);

  if (!canManage) {
    return (
      <div className="p-6 text-center text-[#8F9485]" data-testid="qr-codes-no-access">
        QR Code management is admin/manager only.
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto" data-testid="qr-codes-page">
      <div className="flex items-start sm:items-center justify-between flex-col sm:flex-row gap-3 mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-[#1A1C16] flex items-center gap-2">
            <QrCode className="w-7 h-7 text-[#556B2F]" /> Price List Images
          </h1>
          <p className="text-sm text-[#5C6150] mt-1">
            Every price-list image behind a permanent QR. Update, print, or replace any image — the printed sticker keeps working.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {items.length > 0 && (
            <Button
              onClick={openBulkPrint}
              variant="outline"
              disabled={selectedIds.size === 0}
              className="border-[#556B2F] text-[#556B2F] hover:bg-[#556B2F]/10 disabled:opacity-50"
              data-testid="qr-bulk-print-btn"
              title={selectedIds.size === 0 ? 'Select some images first using the checkbox on each card' : ''}
            >
              <Layers className="w-4 h-4 mr-1" />
              {selectedIds.size === 0 ? 'Bulk Print' : `Bulk Print (${selectedIds.size})`}
            </Button>
          )}
          <Button onClick={() => setShowCreate(true)} className="bg-[#556B2F] hover:bg-[#465926] text-white" data-testid="qr-create-btn">
            <Plus className="w-4 h-4 mr-1" /> Add Price List Image
          </Button>
        </div>
      </div>

      {loading ? (
        <p className="text-[#8F9485]">Loading…</p>
      ) : items.length === 0 ? (
        <Card className="border-dashed border-[#E2E6D9]">
          <CardContent className="p-10 text-center">
            <QrCode className="w-12 h-12 text-[#8F9485] mx-auto mb-3" />
            <p className="text-[#5C6150] mb-4">No price-list images yet. Add your first one.</p>
            <Button onClick={() => setShowCreate(true)} className="bg-[#556B2F] hover:bg-[#465926] text-white">
              <Plus className="w-4 h-4 mr-1" /> Add Price List Image
            </Button>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Search bar + bulk-selection helpers */}
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-4">
            <div className="relative max-w-md flex-1">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#8F9485] pointer-events-none" />
              <Input
                type="search"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder={`Filter ${items.length} image${items.length === 1 ? '' : 's'} by label…`}
                className="pl-9"
                data-testid="qr-search-input"
              />
              {search && (
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-[#8F9485]">
                  {filteredItems.length} match{filteredItems.length === 1 ? '' : 'es'}
                </span>
              )}
            </div>
            <div className="flex items-center gap-2 text-xs">
              <span className="text-[#5C6150]">
                <strong className="text-[#1A1C16]">{selectedIds.size}</strong> selected
              </span>
              <button
                onClick={selectAllVisible}
                className="text-[#556B2F] hover:underline px-1"
                data-testid="select-all-btn"
                disabled={filteredItems.length === 0}
                title="Selects every image matching the current filter (across all pages)"
              >
                Select all{search ? ' matching' : ''} ({filteredItems.length})
              </button>
              {selectedIds.size > 0 && (
                <button
                  onClick={clearSelection}
                  className="text-[#B91C1C] hover:underline px-1"
                  data-testid="clear-selection-btn"
                >
                  Clear
                </button>
              )}
            </div>
          </div>
          {filteredItems.length === 0 ? (
            <p className="text-sm text-[#8F9485] italic">No images match "{search}".</p>
          ) : (
        // IMAGE-FIRST DASHBOARD — big preview, label, and actions on each card
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
          {pageItems.map(it => (
            <Card key={it.id}
              className={`border-[#E2E6D9] overflow-hidden group hover:shadow-md transition-shadow flex flex-col ${selectedIds.has(it.id) ? 'ring-2 ring-[#556B2F] ring-offset-1' : ''}`}
              data-testid={`qr-card-${it.id}`}>
              {/* Selection checkbox — sits over the image, top-left */}
              <div className="relative">
                <button
                  type="button"
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleSelected(it.id); }}
                  className={`absolute top-2 left-2 z-10 w-6 h-6 rounded border-2 flex items-center justify-center transition-colors ${
                    selectedIds.has(it.id)
                      ? 'bg-[#556B2F] border-[#556B2F] text-white'
                      : 'bg-white/85 border-[#5C6150] hover:bg-white text-transparent'
                  }`}
                  aria-label={selectedIds.has(it.id) ? 'Deselect' : 'Select for bulk print'}
                  data-testid={`qr-select-${it.id}`}
                  title="Select for Bulk Print"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
                  </svg>
                </button>
                {/* Big image preview — clicking opens full-screen view */}
                <a
                  href={`/qr/${it.id}`}
                  target="_blank"
                  rel="noreferrer"
                  className="block aspect-square bg-[#F5F6F0] border-b border-[#E2E6D9] overflow-hidden relative"
                  title="Open full-size"
                >
                  {thumbs[it.id] ? (
                    <img
                      src={thumbs[it.id]}
                      alt={it.label}
                      className="w-full h-full object-contain group-hover:scale-[1.02] transition-transform"
                    />
                  ) : (it.content_type || '').toLowerCase() === 'application/pdf' ? (
                    <div className="w-full h-full flex flex-col items-center justify-center text-[#5C6150] gap-2">
                      <FileText className="w-12 h-12 text-[#B91C1C]" />
                      <span className="text-xs font-medium uppercase tracking-wide">PDF</span>
                    </div>
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-xs text-[#8F9485]">
                      Loading…
                    </div>
                  )}
                  <span className="absolute top-2 right-2 bg-black/60 text-white text-[10px] px-1.5 py-0.5 rounded">
                    Open
                  </span>
                </a>
              </div>

              <CardContent className="p-3 flex flex-col flex-1">
                {/* Label + inline edit */}
                <div className="flex items-start justify-between gap-1 mb-1">
                  <h3
                    className="font-semibold text-sm text-[#1A1C16] line-clamp-2 leading-tight flex-1"
                    title={it.label}
                  >
                    {it.label || <span className="italic text-[#8F9485]">(no label)</span>}
                  </h3>
                  <button
                    onClick={() => { setEditing(it); setEditLabel(it.label || ''); }}
                    className="p-1 rounded hover:bg-[#F5F6F0] text-[#5C6150]"
                    title="Edit label"
                    data-testid={`qr-edit-btn-${it.id}`}
                  >
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                </div>
                <p className="text-[10px] text-[#8F9485] mb-3">
                  Updated {formatISTDate(it.updated_at)}
                </p>

                {/* Action row — 4 equally-spaced buttons */}
                <div className="mt-auto grid grid-cols-2 gap-1.5">
                  <Button
                    size="sm"
                    className="bg-[#DAA520] hover:bg-[#B8860B] text-white text-xs h-8 px-2"
                    onClick={() => replaceFileRef.current[it.id]?.click()}
                    data-testid={`qr-replace-btn-${it.id}`}
                    title="Upload a new image (QR stays the same)"
                  >
                    <RefreshCw className="w-3.5 h-3.5 mr-1" /> Update
                  </Button>
                  <input
                    ref={el => { replaceFileRef.current[it.id] = el; }}
                    type="file" accept="image/*,application/pdf" className="hidden"
                    onChange={e => handleReplaceImage(it.id, e.target.files?.[0])}
                  />
                  <Button
                    size="sm"
                    className="bg-[#556B2F] hover:bg-[#465926] text-white text-xs h-8 px-2"
                    onClick={() => { setPrintCfg(c => ({ ...c, label_override: it.label || '' })); setPrinting(it); }}
                    data-testid={`qr-print-btn-${it.id}`}
                  >
                    <Printer className="w-3.5 h-3.5 mr-1" /> Print
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-[#1A1C16] text-[#1A1C16] hover:bg-[#1A1C16]/5 text-xs h-8 px-2"
                    onClick={() => openQrPreview(it)}
                    data-testid={`qr-show-btn-${it.id}`}
                    title="Show QR — scan from your phone to verify"
                  >
                    <ScanLine className="w-3.5 h-3.5 mr-1" /> QR
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-[#B91C1C] text-[#B91C1C] hover:bg-[#FEF2F2] text-xs h-8 px-2"
                    onClick={() => handleDelete(it.id)}
                    data-testid={`qr-delete-btn-${it.id}`}
                  >
                    <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
          )}
          {/* Pagination controls — only when more than one page */}
          {filteredItems.length > PAGE_SIZE && (
            <div className="flex items-center justify-center gap-2 mt-6" data-testid="qr-pagination">
              <Button
                variant="outline" size="sm"
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page <= 1}
                data-testid="qr-page-prev"
              >
                ← Prev
              </Button>
              <span className="text-sm text-[#5C6150] px-3">
                Page <strong className="text-[#1A1C16]">{page}</strong> of {totalPages}
                <span className="text-[#8F9485] ml-2 text-xs">
                  ({filteredItems.length} image{filteredItems.length === 1 ? '' : 's'})
                </span>
              </span>
              <Button
                variant="outline" size="sm"
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
                data-testid="qr-page-next"
              >
                Next →
              </Button>
            </div>
          )}
        </>
      )}

      {/* CREATE dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent>
          <DialogHeader><DialogTitle>Add Price List Image</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Label (printed below QR)</Label>
              <Input value={newLabel} onChange={e => setNewLabel(e.target.value)} placeholder="e.g. Laminates Price List" data-testid="qr-new-label-input" />
            </div>
            <div>
              <Label>Price list image or PDF</Label>
              <Input ref={newFileRef} type="file" accept="image/*,application/pdf" data-testid="qr-new-file-input" />
              <p className="text-[11px] text-[#8F9485] mt-1">
                Accepts JPG / PNG / WebP / GIF or a multi-page PDF. PDFs are shown inline when scanned.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button onClick={handleCreate} disabled={busy} className="bg-[#556B2F] hover:bg-[#465926] text-white" data-testid="qr-new-save-btn">
              {busy ? 'Creating…' : 'Create QR'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* EDIT LABEL dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Edit Label</DialogTitle></DialogHeader>
          <Input value={editLabel} onChange={e => setEditLabel(e.target.value)} autoFocus data-testid="qr-edit-label-input" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={handleSaveLabel} disabled={busy} className="bg-[#556B2F] hover:bg-[#465926] text-white">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* PRINT dialog */}
      <Dialog open={!!printing} onOpenChange={(o) => !o && setPrinting(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Print Sticker Sheet</DialogTitle></DialogHeader>
          {printing && (
            <div className="space-y-3 text-sm">
              <div>
                <Label>Text below each QR (auto-fits to QR width)</Label>
                <Input
                  value={printCfg.label_override}
                  onChange={e => setPrintCfg(c => ({ ...c, label_override: e.target.value }))}
                  placeholder="e.g. LAMINATES"
                  data-testid="qr-print-label-input"
                />
                <p className="text-[11px] text-[#8F9485] mt-1">
                  Font shrinks so the longest line fits the QR width. Shorter text stays smaller — never wider than the QR.
                </p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Sticker width (mm)</Label>
                  <Input type="number" step="0.5" min="8" value={printCfg.sticker_w_mm}
                    onChange={e => setPrintCfg(c => ({ ...c, sticker_w_mm: parseFloat(e.target.value) || 15 }))}
                    data-testid="qr-print-w-input" />
                </div>
                <div>
                  <Label>Sticker height (mm)</Label>
                  <Input type="number" step="0.5" min="8" value={printCfg.sticker_h_mm}
                    onChange={e => setPrintCfg(c => ({ ...c, sticker_h_mm: parseFloat(e.target.value) || 20 }))}
                    data-testid="qr-print-h-input" />
                </div>
                <div>
                  <Label>Page margin (mm)</Label>
                  <Input type="number" step="0.5" min="0" value={printCfg.margin_mm}
                    onChange={e => setPrintCfg(c => ({ ...c, margin_mm: parseFloat(e.target.value) || 0 }))} />
                </div>
                <div>
                  <Label>Gap between stickers (mm)</Label>
                  <Input type="number" step="0.5" min="0" value={printCfg.gap_mm}
                    onChange={e => setPrintCfg(c => ({ ...c, gap_mm: parseFloat(e.target.value) || 0 }))} />
                </div>
                <div className="col-span-2">
                  <Label>How many stickers? (0 = fill one A4 page)</Label>
                  <Input type="number" min="0" value={printCfg.count}
                    onChange={e => setPrintCfg(c => ({ ...c, count: parseInt(e.target.value || '0', 10) }))}
                    data-testid="qr-print-count-input" />
                </div>
              </div>
              <p className="text-xs text-[#8F9485]">
                At {printCfg.sticker_w_mm}×{printCfg.sticker_h_mm}mm — QR will be ~{Math.min(printCfg.sticker_w_mm, printCfg.sticker_h_mm * 0.72).toFixed(1)}mm wide.
                Anything under 10mm may be hard to scan.
              </p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPrinting(null)}>Cancel</Button>
            <Button onClick={handlePrint} disabled={busy} className="bg-[#556B2F] hover:bg-[#465926] text-white" data-testid="qr-print-go-btn">
              <Printer className="w-4 h-4 mr-1" /> {busy ? 'Generating…' : 'Download PDF'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* QR PREVIEW dialog — shows the QR as a big image so user can scan from another phone */}
      <Dialog open={!!previewing} onOpenChange={(o) => !o && closeQrPreview()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>QR Code — {previewing?.label || '(no label)'}</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col items-center gap-3 py-2">
            <div
              ref={qrCanvasRef}
              className="bg-white p-3 rounded border border-[#E2E6D9]"
              data-testid="qr-preview-image"
            >
              {previewing && (
                <QRCodeCanvas
                  value={previewing.qr_target_url}
                  size={272}
                  level="M"
                  marginSize={2}
                />
              )}
            </div>
            <p className="text-xs text-[#5C6150] text-center px-4 break-all">
              {previewing?.qr_target_url}
            </p>
            <p className="text-[11px] text-[#8F9485] text-center max-w-xs">
              Open your phone camera and scan this to verify the link works. You must be logged into HEDA on that device to view the image.
            </p>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => previewing && copyUrl(previewing.qr_target_url)}>
              <Copy className="w-4 h-4 mr-1" /> Copy URL
            </Button>
            <Button onClick={downloadQrPng} disabled={!previewing} variant="outline">
              <Download className="w-4 h-4 mr-1" /> Download PNG
            </Button>
            <Button onClick={closeQrPreview} className="bg-[#556B2F] hover:bg-[#465926] text-white">
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* BULK PRINT dialog — combine multiple QRs onto one A4 sheet */}
      <Dialog open={showBulkPrint} onOpenChange={(o) => !o && setShowBulkPrint(false)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Bulk Print — Combine multiple price-lists on one A4</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 max-h-[65vh] overflow-y-auto pr-2">
            {/* Shared sticker size */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 p-3 bg-[#F5F6F0] rounded">
              <div>
                <Label className="text-xs">Sticker W (mm)</Label>
                <Input type="number" step="0.5" min="8" value={bulkSize.sticker_w_mm}
                  onChange={e => setBulkSize(s => ({ ...s, sticker_w_mm: parseFloat(e.target.value) || 15 }))}
                  data-testid="bulk-w-input" />
              </div>
              <div>
                <Label className="text-xs">Sticker H (mm)</Label>
                <Input type="number" step="0.5" min="8" value={bulkSize.sticker_h_mm}
                  onChange={e => setBulkSize(s => ({ ...s, sticker_h_mm: parseFloat(e.target.value) || 20 }))}
                  data-testid="bulk-h-input" />
              </div>
              <div>
                <Label className="text-xs">Margin (mm)</Label>
                <Input type="number" step="0.5" min="0" value={bulkSize.margin_mm}
                  onChange={e => setBulkSize(s => ({ ...s, margin_mm: parseFloat(e.target.value) || 0 }))} />
              </div>
              <div>
                <Label className="text-xs">Gap (mm)</Label>
                <Input type="number" step="0.5" min="0" value={bulkSize.gap_mm}
                  onChange={e => setBulkSize(s => ({ ...s, gap_mm: parseFloat(e.target.value) || 0 }))} />
              </div>
            </div>

            {/* One row per image — count + text below */}
            <div className="space-y-2">
              <div className="grid grid-cols-[40px_1fr_90px_2fr] gap-2 text-[11px] font-medium text-[#5C6150] uppercase tracking-wide px-1">
                <span>Img</span>
                <span>Saved label</span>
                <span className="text-center">Count</span>
                <span>Text on sticker</span>
              </div>
              {items.filter(it => bulkRows[it.id]).map(it => {
                const row = bulkRows[it.id];
                return (
                  <div key={it.id}
                    className={`grid grid-cols-[40px_1fr_90px_2fr] gap-2 items-center p-1.5 rounded ${row.count > 0 ? 'bg-[#DAA520]/10' : ''}`}
                    data-testid={`bulk-row-${it.id}`}>
                    <div className="w-9 h-9 rounded overflow-hidden bg-[#F5F6F0] border border-[#E2E6D9] flex-shrink-0">
                      {thumbs[it.id] && <img src={thumbs[it.id]} alt="" className="w-full h-full object-cover" />}
                    </div>
                    <span className="text-xs text-[#1A1C16] truncate" title={it.label}>{it.label || <em className="text-[#8F9485]">(no label)</em>}</span>
                    <Input
                      type="number" min="0" step="1"
                      value={row.count}
                      onChange={e => updateBulkRow(it.id, { count: parseInt(e.target.value || '0', 10) })}
                      className="text-center h-9"
                      data-testid={`bulk-count-${it.id}`}
                    />
                    <Input
                      value={row.label_override}
                      onChange={e => updateBulkRow(it.id, { label_override: e.target.value })}
                      placeholder={it.label || '(blank)'}
                      className="h-9 text-sm"
                      data-testid={`bulk-label-${it.id}`}
                    />
                  </div>
                );
              })}
            </div>

            <div className="text-xs text-[#5C6150] bg-[#F5F6F0] rounded p-2 flex justify-between items-center">
              <span>Total stickers: <strong className="text-[#1A1C16]">{bulkTotal}</strong></span>
              <span className="text-[#8F9485]">
                Each row uses its own text. Font size is uniform across the page (auto-fits longest text to QR width).
              </span>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBulkPrint(false)}>Cancel</Button>
            <Button onClick={handleBulkPrint} disabled={busy || bulkTotal === 0}
              className="bg-[#556B2F] hover:bg-[#465926] text-white" data-testid="bulk-print-go-btn">
              <Printer className="w-4 h-4 mr-1" /> {busy ? 'Generating…' : `Download PDF (${bulkTotal})`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
